import { Menu } from "@mui/material";
import { withStyles, WithStyles } from "@mui/styles";
import * as api from "api/Core";
import { DefaultListParameters } from "assets/appConfig";
import { FeedStyleClasses, TimelineStyleClasses } from "assets/jss/common-styles";
import AdvFilter, { AdvFilterComponent, AdvFilterResult } from "components/AdvFilter";
import ButtonOrderBy from "components/Buttons/ButtonOrderBy";
import ButtonResetFilter from "components/Buttons/ButtonResetFilter";
import { LoadingIndicator } from "components/CallServer/LoadingIndicator";
import { RequestErrorReport } from "components/CallServer/RequestErrorReport";
import CountFilter from "components/CountFilter";
import { MuiFilterAdvancedContainer, MuiFilterAppBar, MuiFilterToolbar } from "components/MuiExtras/Filter";
import MuiMessageBox from "components/MuiExtras/MessageBox/MuiMessageBox";
import MuiContainer from "components/MuiExtras/MuiContainer";
import MuiMenuItemChecked from "components/MuiExtras/MuiMenuItemChecked";
import MuiPagination from "components/MuiExtras/MuiPagination";
import MuiPushRight from "components/MuiExtras/MuiPushRight";
import { withSnackbar, WithSnackbar } from "components/SnackbarProvider/Snackbar";
import HtmlUtils from "lib/HtmlUtils";
import LibUtils from "lib/LibUtils";
import { GetTopicListParams, PagedResult, TopicListOrderBy, TopicUserFlowListItemDto } from "models/ApiModels";
import * as React from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import { RouteComponentProps, withRouter } from "react-router";
import { RoutePaths } from "routes/RouteConfig";
import { SidebarPostWindowMessages } from "store/sidebar/windowMessages";
import TopicCardFollow from "views/Topic/Components/TopicCardFollow";
import TopicEdit from "views/Topic/Components/TopicEdit";

/////////////////////////////////////////////////////////////////////////////////////////////////

interface IState {
    searchText: string;
    orderByMenuAnchorEl: any;
    dialog: any;
    list: PagedResult<TopicUserFlowListItemDto>;
    offset: number;
    loading: boolean;
    lastError: any;
    editTopicId: string;
}

interface IProps {
}

interface IRouteProps {
    id: string;
}

type Props = IProps & RouteComponentProps<IRouteProps> & WithStyles<typeof TimelineStyleClasses> & WithStyles<typeof FeedStyleClasses> & WithSnackbar & WithTranslation;

/////////////////////////////////////////////////////////////////////////////////////////////////

class TopicFollowList extends React.PureComponent<Props, IState> {
    private advFilterRef: AdvFilterComponent;
    private orderBy: TopicListOrderBy = TopicListOrderBy.Title;
    private sidebarWindowMessages: SidebarPostWindowMessages;

    ///
    constructor(props: Props) {
        super(props);
        this.state = {
            searchText: null,
            list: { items: null, total: 0 },
            offset: 0,
            orderByMenuAnchorEl: null,
            loading: false,
            lastError: null,
            dialog: null,
            editTopicId: this.props.match.params.id
        };

        this.sidebarWindowMessages = new SidebarPostWindowMessages(window.opener || window);
    }

    ///
    private res(r: string) {
        return this.props.t("Views.Topic.TopicFollowList." + r);
    }

    ///
    private isFiltered() {
        return this.state.searchText != null;
    }

    ///
    private applyOrder = (orderBy: TopicListOrderBy) => {
        this.orderBy = orderBy;
        this.loadData(0);
    };

    ///
    private loadData(offset: number) {

        const p: GetTopicListParams = {
            searchText: this.state.searchText,
            orderBy: this.orderBy,
            range: { offset: offset, take: DefaultListParameters.viewRecordsByPage }
        };

        this.setState({ loading: true, orderByMenuAnchorEl: null, dialog: null, editTopicId: null });

        api.getTopicListToUser(p)
            .then((result) => {
                this.setState({ list: result, loading: false, offset: offset });
            })
            .catch((error) => {
                this.setState({ list: null, lastError: error, loading: false, offset: 0 });
            });
    }

    ///
    private handleUpdateFilter = (searchText: string, advFilters: AdvFilterResult[]) => {
        this.setState({
            searchText: searchText.length == 0 ? null : searchText
        }, () => this.loadData(0));
    };

    ///
    private handleViewFeed(topicId: string) {
        HtmlUtils.openUrlInNewWindow(RoutePaths.TimelineTopic.replace(":id", topicId));
    }

    ///
    private renderAdvFilter(): JSX.Element {
        return (
            <MuiFilterAppBar position="static">
                <MuiFilterToolbar variant="dense" >

                    <MuiFilterAdvancedContainer>

                        <AdvFilter
                            id="advfilter"
                            commands={[]}
                            innerRef={(ref) => this.advFilterRef = ref}
                            selectedCommands={[]}
                            placeHolder={this.props.t("Common.Filter.Adv.InputPlaceholder")}
                            placeHolderFocus={this.props.t("Common.Filter.Adv.InputPlaceholder")}
                            title={this.props.t("Common.Filter.Adv.InputTitleEnterToFilter")}
                            onUpdateSelection={this.handleUpdateFilter}
                        />

                        <MuiPushRight>
                            <ButtonOrderBy
                                onClick={
                                    (e) => {
                                        this.setState({ orderByMenuAnchorEl: e.currentTarget });
                                    }
                                }
                            />
                            <Menu
                                id="simple-menu"
                                anchorEl={this.state.orderByMenuAnchorEl}
                                open={this.state.orderByMenuAnchorEl != null}
                                onClose={
                                    () => {
                                        this.setState({ orderByMenuAnchorEl: null });
                                    }}
                            >
                                <MuiMenuItemChecked text={this.props.t("Common.Filter.Toolbar.OrderBy.Name")} value={TopicListOrderBy.Title} currentSelection={this.orderBy} onClick={this.applyOrder} />
                                <MuiMenuItemChecked text={this.props.t("Common.Filter.Toolbar.OrderBy.MorePublications")} value={TopicListOrderBy.Publications} currentSelection={this.orderBy} onClick={this.applyOrder} />
                                <MuiMenuItemChecked text={this.props.t("Common.Filter.Toolbar.OrderBy.MoreFollowers")} value={TopicListOrderBy.Followers} currentSelection={this.orderBy} onClick={this.applyOrder} />
                                <MuiMenuItemChecked text={this.props.t("Common.Filter.Toolbar.OrderBy.Following")} value={TopicListOrderBy.Following} currentSelection={this.orderBy} onClick={this.applyOrder} />
                                <MuiMenuItemChecked text={this.props.t("Common.Filter.Toolbar.OrderBy.Unfollowing")} value={TopicListOrderBy.Unfollowing} currentSelection={this.orderBy} onClick={this.applyOrder} />
                            </Menu>
                        </MuiPushRight>

                    </MuiFilterAdvancedContainer>

                </MuiFilterToolbar>
            </MuiFilterAppBar>
        );
    }

    ///
    private handleFollow = (item: TopicUserFlowListItemDto) => {
        api.followTopic(item.topicId, !item.following)
            .then((result) => {
                if (result) {

                    this.setState((state) => {

                        const newItems = state.list.items.slice(0);
                        var changedItem = newItems.find(p => p.topicId == item.topicId);
                        if (changedItem) {
                            changedItem.following = !changedItem.following;
                        }

                        return {
                            ...state,
                            ...{
                                list: {
                                    items: newItems,
                                    total: state.list.total
                                }
                            }
                        };
                    });

                    if (item.following)
                        this.props.snackbar.showMessage(this.res("FollowSuccess"), "success");
                    else
                        this.props.snackbar.showMessage(this.res("UnfollowSuccess"), "warning");

                    this.sidebarWindowMessages.reset();
                };
            });
    };

    ///
    private renderItems() {
        const { list } = this.state;

        return (
            <div>
                {
                    list && list.total > 0 &&
                    <MuiContainer margin="normal">
                        <CountFilter
                            count={list.total}
                            filtered={this.isFiltered()}
                            resource="Views.Topic.TopicAdminList.RsResultText"
                            filteredResource="Views.Topic.TopicAdminList.RsFilteredResultText"
                            onResetFilter={() => this.advFilterRef.resetFilter()}
                        />
                    </MuiContainer>
                }

                {
                    this.state.list.items.map((item, index) => (
                        <MuiContainer margin="dense" key={index} >
                            <TopicCardFollow
                                topic={item}
                                key={index}
                                onViewFeed={() => this.handleViewFeed(item.topicId)}
                                onFollow={() => this.handleFollow(item)}
                                classes={this.props.classes}
                            />
                        </MuiContainer>
                    ))
                }
                <MuiPagination
                    limit={DefaultListParameters.viewRecordsByPage}
                    offset={this.state.offset}
                    total={this.state.list.total}
                    onClick={(e, offset) => this.loadData(offset)}
                />
            </div>
        );
    }

    ///
    private renderList() {
        const { list, lastError, loading } = this.state;

        if (lastError)
            return <RequestErrorReport error={lastError} />;

        return (
            <>
                {this.state.dialog}

                {
                    (loading || list.total > 0 || this.isFiltered()) &&
                    <MuiContainer margin="normal">
                        {this.renderAdvFilter()}
                    </MuiContainer>
                }

                {
                    loading &&
                    <LoadingIndicator />
                }

                {
                    !loading && list.total == 0 && !this.isFiltered()
                        ? <MuiMessageBox variant="success" message={this.res("FollowingAllTopicsMessage")} />
                        : list.total == 0 && !loading && this.isFiltered()
                            ? (
                                <MuiMessageBox
                                    variant="warning"
                                    action={<ButtonResetFilter color="warning" onClick={() => this.advFilterRef.resetFilter()} />}
                                    message={this.props.t("Views.Topic.TopicAdminList.RsFilteredResultText_zero")}
                                />
                            )
                            : !loading
                                ? this.renderItems()
                                : null
                }

            </>
        );
    }

    ///
    public componentDidMount() {
        if (!this.props.match.params.id)
            this.loadData(0);
    }

    ///
    public componentDidUpdate(prevProps: Props) {
        if (!this.props.match.params.id && prevProps.match.params.id)
            this.loadData(0);
    }

    ///
    public render() {
        const { editTopicId } = this.state;

        if (editTopicId != null)
            return (
                <TopicEdit
                    topicId={editTopicId}
                    onClose={
                        (result) => {
                            if (this.props.match.params.id)
                                this.props.history.replace(LibUtils.removeRouterOptionalParameters(RoutePaths.TopicAdminList));
                            else if (result != undefined || this.state.list.items == null)
                                this.loadData(this.state.offset);
                            else
                                this.setState({ editTopicId: null });
                        }
                    }
                />
            );

        return (
            <div className={this.props.classes.timelineContainer}>
                {this.renderList()}
            </div>
        );
    }
}

/////////////////////////////////////////////////////////////////////////////////////////////////

const withAllStyles = withStyles(
    (theme) => ({
        ...TimelineStyleClasses(theme),
        ...FeedStyleClasses(theme),
    }),
    { withTheme: true },
);

export default withRouter(withSnackbar(withTranslation()(withAllStyles(TopicFollowList))));
